import { Component, input, output, computed, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppSettings } from '@services/settings/app-settings.service';
import { CatalogService } from '@services/data/catalog.service';
import { StorageService } from '@services/core/storage.service';
import { BpmService } from 'app/feature/management-request/services/bpm.service';
import { Request, RequestService } from 'app/feature/management-request/services/requests.service';
import Swal from 'sweetalert2';

export interface FlowStepCard {
  id: string;
  title: string;
  icon: string;
  statusLabel: string;
  statusStyle?: string;
  statusSlug?: string;
  state: 'ok' | 'pending' | 'rejected';
  actorName?: string | null;
  actorMail?: string | null;
  actorRole?: string | null;
  decidedAt?: string | null;
  channel?: string | null;
  token?: string | null;
  tokenDisplay?: string | null;
  votoNumber?: string | null;
  amount?: number | null;
  transactionNumber?: string | null;
  transferLabel?: string | null;
  hasReceipt?: boolean;
  rejectionReason?: string | null;
  pendingMessage?: string | null;
  hasDocument?: boolean;
  documentLabel?: string | null;
}

@Component({
  selector: 'app-flow-steps',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './flow-steps.component.html',
  styleUrls: ['./flow-steps.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class FlowStepsComponent {
  public appSettings = inject(AppSettings);
  private catalogService = inject(CatalogService);
  private bpmService = inject(BpmService);
  private requestsService = inject(RequestService);
  private storageService = inject(StorageService);

  request = input<Request | null | undefined>(null);
  customSteps = input<FlowStepCard[] | null>(null);
  /**
   * auto = móvil→timeline, escritorio→tabla.
   * timeline = riel vertical (útil en columnas angostas, p.ej. Aprobación + PDF).
   * table = tabla ejecutiva.
   */
  layout = input<'auto' | 'timeline' | 'table'>('auto');
  viewDocument = output<FlowStepCard>();
  tokenCopied = signal(false);
  tokenResending = signal(false);
  private tokenTimer: ReturnType<typeof setTimeout> | null = null;

  useTimeline = computed(() => {
    const layout = this.layout();
    if (layout === 'timeline') return true;
    if (layout === 'table') return false;
    return this.appSettings.isMobile();
  });

  /** Solo ADM reenvía el correo de token (recuperación puntual). */
  canResendToken(): boolean {
    const role = String(this.storageService.getJsonValue('role')?.name ?? '').trim().toUpperCase();
    return role === 'ADM';
  }

  /** Pasos: custom → BPM statusFlow + estilos del catálogo → fallback legado. */
  resolvedSteps = computed<FlowStepCard[]>(() => {
    const custom = this.customSteps();
    if (custom?.length) {
      return custom.map((s) => this.enrichCustomStep(s));
    }

    const req = this.request();
    if (!req) return [];

    const fromFlow = this.buildFromBpmFlow(req);
    if (fromFlow.length) return fromFlow;

    return this.buildLegacySteps(req);
  });

  /**
   * Nombre, estilo e icono desde status_ref.
   * Fallback mínimo solo si el catálogo aún no cargó ese slug.
   */
  private resolveStatusInfo(slug: string | null | undefined, fallbackName?: string) {
    const key = String(slug || '').trim();
    const status = key ? this.catalogService.getStatusBySlug(key) : null;
    return {
      label: status?.name ?? fallbackName ?? (key || '—'),
      style: status?.style || this.fallbackStyle(key),
      icon: String(status?.icon || '').trim() || this.fallbackIcon(key),
      slug: key || undefined
    };
  }

  private fallbackStyle(slug: string): string {
    const s = slug.toUpperCase();
    if (s === 'REJECTED') return 'border-danger text-danger';
    if (['PAID', 'REIMBURSED', 'APPROVED', 'MINISTRY_APPROVED', 'ACTIVE'].includes(s)) {
      return 'border-success text-success';
    }
    if (s.includes('PENDING') || s === 'REVIEWING' || s === 'OBSERVED' || s === 'DRAFT') {
      return 'border-warning text-warning';
    }
    return 'border text-muted';
  }

  private fallbackIcon(slug: string): string {
    const s = slug.toUpperCase();
    if (s === 'REJECTED') return 'fas fa-times-circle';
    if (s === 'OBSERVED') return 'fas fa-exclamation-triangle';
    if (s === 'REVIEWING') return 'fas fa-search';
    if (['PAID', 'REIMBURSED'].includes(s) || s.includes('PAYMENT')) return 'fas fa-money-check-alt';
    if (['APPROVED', 'MINISTRY_APPROVED', 'ACTIVE'].includes(s)) return 'fas fa-check-circle';
    if (s.includes('PENDING') || s === 'DRAFT') return 'fas fa-clock';
    return 'fas fa-circle';
  }

  /**
   * Badges de Estado:
   * pending → secondary; ok → success; rejected → danger; observado → warning.
   */
  private resolveBadgeStyle(
    state: FlowStepCard['state'],
    badgeSlug: string | null | undefined
  ): string {
    const key = String(badgeSlug || '').toUpperCase();
    if (state === 'rejected' || key === 'REJECTED') {
      return 'border-danger text-danger';
    }
    if (key === 'OBSERVED' || key.includes('OBSERV')) {
      return 'border-warning text-warning';
    }
    if (state === 'pending') {
      return 'border-secondary text-secondary';
    }
    return 'border-success text-success';
  }

  private enrichCustomStep(step: FlowStepCard): FlowStepCard {
    const info = this.resolveStatusInfo(step.statusSlug, step.statusLabel);
    const statusSlug = step.statusSlug || info.slug;
    return {
      ...step,
      statusLabel: step.statusLabel || info.label,
      statusStyle: this.resolveBadgeStyle(step.state, statusSlug),
      statusSlug,
      icon: step.icon || info.icon
    };
  }

  private statusSlugOf(flow: any): string | null {
    return flow?.status?.slug || flow?.statusSlug || null;
  }

  private resolveRoleTitle(roleKey?: string | null, fallback = ''): string {
    const key = roleKey ?? fallback;
    if (!key) return fallback || 'Paso';
    return this.catalogService.getRoleDisplayName(key, key);
  }

  /** Trazabilidad desde process.statusFlow; badge con style del catálogo. */
  private buildFromBpmFlow(req: Request): FlowStepCard[] {
    const flow = this.bpmService.sortedFlowFor(req);
    if (flow.length < 2) return [];

    const currentIdx = this.bpmService.currentFlowIndex(req);
    const rejected = (req.statusSlug || '').toUpperCase() === 'REJECTED';
    const rejectAt = rejected ? this.resolveRejectionIndex(req, flow, currentIdx) : -1;

    const cards: FlowStepCard[] = [];
    const lastVisible = rejected
      ? Math.max(rejectAt, 0)
      : (currentIdx < 0 ? 0 : currentIdx);

    for (let i = 0; i <= lastVisible && i < flow.length; i++) {
      const step = flow[i];
      const slug = this.statusSlugOf(step);
      if (!slug) continue;

      const isTerminal = i === flow.length - 1;
      const isRejectedHere = rejected && i === rejectAt;
      const isPast = !rejected && currentIdx > i;
      const isCurrent = !rejected && currentIdx === i;
      const isDone =
        isPast ||
        (isCurrent && isTerminal && this.bpmService.isClosed(req));

      let state: FlowStepCard['state'] = 'pending';
      let badgeSlug = slug;

      if (isRejectedHere) {
        state = 'rejected';
        badgeSlug = 'REJECTED';
      } else if (isDone) {
        state = 'ok';
        // Estilo/icono del destino; el texto sale de approvedLabel del estado que se aprobó
        badgeSlug = this.statusSlugOf(flow[i + 1]) || slug;
      } else if (isCurrent || (!rejected && currentIdx < 0 && i === 0)) {
        state = 'pending';
        badgeSlug = 'PENDING';
      }

      const stepStatus = this.catalogService.getStatusBySlug(slug);
      const info = this.resolveStatusInfo(
        badgeSlug,
        state === 'pending' ? 'Pendiente' : undefined
      );

      let statusLabel = info.label;
      if (state === 'ok') {
        const approvedLabel = String(stepStatus?.approvedLabel || '').trim();
        statusLabel = approvedLabel
          || this.catalogService.getStatusName(badgeSlug, 'Aprobado');
      }

      // Pendiente/rechazado: icono del paso actual.
      // Aprobado: icono del siguiente estado del flujo (destino).
      let roleIcon: string;
      if (state === 'ok') {
        const nextSlug = this.statusSlugOf(flow[i + 1]) || badgeSlug || slug;
        const nextStatus = this.catalogService.getStatusBySlug(nextSlug);
        roleIcon = String(nextStatus?.icon || info.icon || '').trim()
          || this.fallbackIcon(nextSlug);
      } else if (state === 'rejected') {
        roleIcon = this.fallbackIcon('REJECTED');
      } else {
        roleIcon = String(stepStatus?.icon || '').trim() || this.fallbackIcon(slug);
      }

      const roleKey = step.requiredRole || step.contentReviewRole || null;
      const roleTitle = (roleKey ? this.resolveRoleTitle(roleKey) : '')
        || String(stepStatus?.name || '').trim()
        || 'responsable';

      cards.push(this.enrichFlowCard(req, {
        id: `flow-${i}-${slug}`,
        title: roleTitle,
        icon: roleIcon,
        statusLabel,
        statusStyle: this.resolveBadgeStyle(state, badgeSlug),
        statusSlug: badgeSlug,
        state,
        pendingMessage: state === 'pending'
          ? (roleKey
            ? `Pendiente de acción por ${roleTitle}.`
            : 'Pendiente de acción.')
          : null,
        rejectionReason: isRejectedHere ? (req.rejectionReason ?? null) : null
      }, step, state));
    }

    return cards;
  }

  private resolveRejectionIndex(req: Request, flow: any[], currentIdx: number): number {
    if (req.ministryDecision?.status === 'REJECTED' ||
      (req.rejectionReason && !req.commissionDecision?.decidedAt && !req.claim?.ministroApprovalDate)) {
      return 0;
    }
    if (currentIdx >= 0) return currentIdx;
    return Math.max(flow.length - 2, 0);
  }

  private channelLabel(channel?: string | null): string {
    const c = String(channel || '').toUpperCase();
    if (!c || c === 'INBOX') return 'Bandeja';
    if (c === 'TOKEN') return 'TOKEN';
    if (c === 'SYSTEM') return 'Sistema';
    return channel || 'Bandeja';
  }

  /**
   * Historial BPM (request_flow_step) para el paso: transición saliendo de fromSlug.
   * Sirve para MEMBERSHIP/BENEFIT/etc. donde no hay ministryDecision de claim.
   */
  private findHistoryForStep(
    req: Request,
    fromSlug: string,
    toSlug?: string | null
  ): NonNullable<Request['flowHistory']>[number] | null {
    const history = req.flowHistory ?? [];
    if (!history.length) return null;
    const from = String(fromSlug || '').toUpperCase();
    const decided = history
      .filter((h) => {
        const hFrom = String(h.fromStatusSlug || '').toUpperCase();
        const st = String(h.status || '').toUpperCase();
        return hFrom === from && !!h.decidedAt && st !== 'PENDING' && st !== 'SUPERSEDED';
      })
      .sort((a, b) => new Date(b.decidedAt || 0).getTime() - new Date(a.decidedAt || 0).getTime());

    if (toSlug) {
      const to = String(toSlug).toUpperCase();
      const match = decided.find((h) => String(h.toStatusSlug || '').toUpperCase() === to);
      if (match) return match;
    }
    return decided[0] ?? null;
  }

  private enrichFlowCard(
    req: Request,
    base: FlowStepCard,
    flowStep: any,
    state: FlowStepCard['state']
  ): FlowStepCard {
    const stepSlug = (this.statusSlugOf(flowStep) || '').toUpperCase();
    const role = String(flowStep?.requiredRole || flowStep?.contentReviewRole || '').toLowerCase();
    const md = req.ministryDecision;
    const cd = req.commissionDecision;
    const pd = req.paymentDecision;
    const resultSlug = String(base.statusSlug || '').toUpperCase();
    const history = (state === 'ok' || state === 'rejected')
      ? this.findHistoryForStep(req, stepSlug, resultSlug === 'PENDING' ? null : resultSlug)
        ?? this.findHistoryForStep(req, stepSlug, null)
      : null;

    const isMinistryRole = role.includes('ministro') || (!role && stepSlug === 'PENDING');
    const isPaymentRole = role.includes('fi') || role.includes('tesor')
      || ['PAID', 'REIMBURSED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT'].includes(stepSlug)
      || /PAGO|PAYMENT|RECEIPT|COMPROBANTE|REEMBOLSO/i.test(String(flowStep?.documentTypeSlug || ''));

    if (state === 'pending' && (isMinistryRole || this.bpmService.isInitialStep(req))) {
      return {
        ...base,
        channel: req.confirmationToken ? 'TOKEN' : 'Bandeja',
        token: req.confirmationToken ?? null,
        tokenDisplay: this.formatToken(req.confirmationToken),
        pendingMessage: base.pendingMessage || 'Pendiente de acción.'
      };
    }

    if (state !== 'ok' && state !== 'rejected') return base;

    // Fuente principal: flowHistory (todos los procesos BPM)
    if (history?.decidedAt || history?.actorLabel) {
      const gloss = history.gloss ?? null;
      const stepHasDoc = !!(history.documentUuid || ((history as any)?.requiresDocument && gloss) || (flowStep?.requiresDocument && gloss));
      const docLabel = stepHasDoc
        ? (history.documentTypeName || (gloss ? `Acta ${gloss}` : 'Ver documento'))
        : null;
      return {
        ...base,
        actorName: history.actorLabel
          ?? req.claim?.ministroApprovedBy
          ?? md?.actorLabel
          ?? cd?.actorLabel
          ?? pd?.actorLabel
          ?? base.actorName,
        actorMail: history.actorMail ?? md?.actorMail ?? cd?.actorMail ?? pd?.actorMail ?? null,
        actorRole: base.title,
        decidedAt: history.decidedAt
          ?? req.claim?.ministroApprovalDate
          ?? md?.decidedAt
          ?? cd?.decidedAt
          ?? pd?.decidedAt
          ?? null,
        channel: this.channelLabel(history.channel ?? md?.channel ?? cd?.channel ?? pd?.channel),
        votoNumber: gloss,
        hasDocument: state === 'ok' && stepHasDoc,
        documentLabel: docLabel,
        rejectionReason: state === 'rejected'
          ? (history.comments ?? req.rejectionReason ?? base.rejectionReason)
          : base.rejectionReason
      };
    }

    if (isMinistryRole || stepSlug === 'PENDING') {
      return {
        ...base,
        actorName: req.claim?.ministroApprovedBy ?? md?.actorLabel ?? base.actorName,
        actorMail: md?.actorMail ?? null,
        actorRole: base.title,
        decidedAt: req.claim?.ministroApprovalDate ?? md?.decidedAt ?? null,
        channel: this.channelLabel(md?.channel) || 'Bandeja'
      };
    }

    if (isPaymentRole) {
      const pay = req.payment;
      const reimb = (req as any).reimbursements?.[0];
      const hasDoc = !!(pay?.document?.uuid || reimb?.documents?.length);
      return {
        ...base,
        actorName: pd?.actorLabel ?? base.actorName,
        actorMail: pd?.actorMail ?? null,
        actorRole: base.title,
        decidedAt: pay?.paymentDate ?? pd?.decidedAt ?? null,
        amount: pay?.amount ?? null,
        channel: this.channelLabel(pd?.channel) || 'Bandeja',
        hasDocument: state === 'ok' && hasDoc,
        documentLabel: hasDoc ? 'Comprobante' : null
      };
    }

    const stepRequiresDoc = !!flowStep?.requiresDocument;
    const gloss = stepRequiresDoc ? (this.bpmService.documentGloss(req) ?? null) : null;
    const hasDoc = !!((cd as any)?.documentUuid || (stepRequiresDoc && gloss));
    return {
      ...base,
      actorName: cd?.actorLabel ?? base.actorName,
      actorMail: cd?.actorMail ?? null,
      actorRole: base.title,
      decidedAt: cd?.decidedAt ?? null,
      channel: this.channelLabel(cd?.channel) || 'Bandeja',
      votoNumber: gloss,
      hasDocument: state === 'ok' && hasDoc,
      documentLabel: hasDoc ? (gloss ? `Acta ${gloss}` : 'Ver Acta') : null
    };
  }

  /** Fallback si el proceso aún no tiene statusFlow en catálogo. */
  private buildLegacySteps(req: Request): FlowStepCard[] {
    const steps: FlowStepCard[] = [];
    const min = this.buildMinistryStep(req);
    if (min) steps.push(min);
    const com = this.buildCommissionStep(req);
    if (com) steps.push(com);
    const pay = this.buildPaymentStep(req);
    if (pay) steps.push(pay);
    return steps;
  }

  private checkMinistryStatus(req: Request) {
    const status = req.statusSlug ?? '';
    const isPending = status === 'PENDING';
    const isRejected = req.ministryDecision?.status === 'REJECTED'
      || (status === 'REJECTED' && !!req.ministryDecision?.decidedAt && !req.claim?.ministroApprovalDate);
    const hasSeal = !!(req.claim?.ministroApprovedBy || req.claim?.ministroApprovalDate);
    const hasDecision = !!(req.ministryDecision?.decidedAt
      && (req.ministryDecision.toStatusSlug === 'MINISTRY_APPROVED' || req.ministryDecision.status === 'CONFIRMED'));
    const isDone = !isPending && (hasSeal || hasDecision) && !isRejected;
    return { isPending, isRejected, isDone };
  }

  private buildMinistryStep(req: Request): FlowStepCard | null {
    const { isPending, isRejected, isDone } = this.checkMinistryStatus(req);
    const slug = isPending ? 'PENDING' : (isRejected ? 'REJECTED' : 'MINISTRY_APPROVED');
    const state: FlowStepCard['state'] = isPending ? 'pending' : (isRejected ? 'rejected' : 'ok');
    const { label, icon } = this.resolveStatusInfo(slug);
    const roleTitle = this.resolveRoleTitle(req.ministryDecision?.actorRole, 'MINISTRO_FE');
    const md = req.ministryDecision;
    return {
      id: 'ministry',
      title: roleTitle,
      icon,
      statusLabel: label,
      statusStyle: this.resolveBadgeStyle(state, slug),
      statusSlug: slug,
      state,
      actorName: isDone ? (req.claim?.ministroApprovedBy ?? md?.actorLabel ?? null) : null,
      actorMail: isDone ? (md?.actorMail ?? null) : null,
      actorRole: isDone ? roleTitle : null,
      decidedAt: isDone ? (req.claim?.ministroApprovalDate ?? md?.decidedAt ?? null) : null,
      channel: md?.channel ?? (isPending && req.confirmationToken ? 'TOKEN' : 'Bandeja'),
      token: isPending ? (req.confirmationToken ?? null) : null,
      tokenDisplay: isPending ? this.formatToken(req.confirmationToken) : null,
      rejectionReason: isRejected ? (req.rejectionReason ?? null) : null,
      pendingMessage: 'Pendiente de visto bueno.'
    };
  }

  private buildCommissionStep(req: Request): FlowStepCard | null {
    const cd = req.commissionDecision;
    const status = req.statusSlug ?? '';
    if (status === 'PENDING') return null;

    const isRejected = status === 'REJECTED' && (!!cd?.decidedAt || !!req.rejectionReason)
      && req.ministryDecision?.status !== 'REJECTED';
    const gloss = this.bpmService.documentGloss(req);
    const isDone = (status === 'APPROVED' || !!gloss || !!cd?.decidedAt) && !isRejected;
    const isPending = !isDone && !isRejected;
    const slug = isPending ? 'PENDING' : (isRejected ? 'REJECTED' : 'APPROVED');
    const state: FlowStepCard['state'] = isPending ? 'pending' : (isRejected ? 'rejected' : 'ok');
    const { label, icon } = this.resolveStatusInfo(slug);
    const roleTitle = this.resolveRoleTitle(cd?.actorRole, 'SEC_UCH');
    const hasDoc = isDone && !!(gloss || (cd as any)?.documentUuid || req.claim?.documents?.length);
    return {
      id: 'commission',
      title: roleTitle,
      icon,
      statusLabel: label,
      statusStyle: this.resolveBadgeStyle(state, slug),
      statusSlug: slug,
      state,
      actorName: isDone ? (cd?.actorLabel ?? null) : null,
      actorMail: isDone ? (cd?.actorMail ?? null) : null,
      actorRole: roleTitle,
      decidedAt: isDone ? (cd?.decidedAt ?? null) : null,
      channel: cd?.channel ?? 'Bandeja',
      votoNumber: isDone ? (gloss || null) : null,
      hasDocument: hasDoc,
      documentLabel: hasDoc ? (gloss ? `Acta ${gloss}` : 'Ver Acta') : null,
      rejectionReason: isRejected ? (req.rejectionReason ?? null) : null,
      pendingMessage: `Pendiente de aprobación por ${roleTitle}.`
    };
  }

  private buildPaymentStep(req: Request): FlowStepCard | null {
    const status = req.statusSlug ?? '';
    if (['PENDING', 'MINISTRY_APPROVED', 'REJECTED'].includes(status)) return null;

    const isPaid = status === 'PAID' || status === 'REIMBURSED';
    const slug = isPaid ? status : (status === 'PENDING_REIMBURSEMENT' ? 'PENDING_REIMBURSEMENT' : 'PENDING_PAYMENT');
    const state: FlowStepCard['state'] = isPaid ? 'ok' : 'pending';
    const { label, icon } = this.resolveStatusInfo(slug);
    const roleTitle = this.resolveRoleTitle(req.paymentDecision?.actorRole, 'fi');
    const pd = req.paymentDecision;
    const pay = req.payment;
    const reimb = (req as any).reimbursements?.[0];
    const hasDoc = isPaid && !!(pay?.document?.uuid || reimb?.documents?.length);
    return {
      id: 'payment',
      title: roleTitle,
      icon,
      statusLabel: label,
      statusStyle: this.resolveBadgeStyle(state, slug),
      statusSlug: slug,
      state,
      actorName: isPaid ? (pd?.actorLabel ?? null) : null,
      actorMail: isPaid ? (pd?.actorMail ?? null) : null,
      actorRole: roleTitle,
      decidedAt: isPaid ? (pay?.paymentDate ?? pd?.decidedAt ?? null) : null,
      amount: pay?.amount ?? null,
      channel: pd?.channel ?? 'Bandeja',
      hasDocument: hasDoc,
      documentLabel: hasDoc ? 'Comprobante' : null,
      pendingMessage: `Pendiente de pago por ${roleTitle}.`
    };
  }

  onViewDoc(step: FlowStepCard): void {
    this.viewDocument.emit(step);
  }

  private formatToken(token?: string | null): string | null {
    if (!token) return null;
    return token.length > 14 ? `${token.slice(0, 6)}...${token.slice(-6)}` : token;
  }

  async copyToken(text?: string | null): Promise<void> {
    if (!text) return;
    try {
      const confirmUrl = `${window.location.origin}/confirm-request?token=${text}`;
      await navigator.clipboard.writeText(confirmUrl);
      this.tokenCopied.set(true);
      if (this.tokenTimer) clearTimeout(this.tokenTimer);
      this.tokenTimer = setTimeout(() => this.tokenCopied.set(false), 2000);
    } catch {
      this.tokenCopied.set(false);
    }
  }

  resendToken(): void {
    const req = this.request();
    const id = req?.uuid;
    if (!id || this.tokenResending() || !this.canResendToken()) return;
    this.tokenResending.set(true);
    this.requestsService.resendToken(id).subscribe({
      next: () => {
        this.tokenResending.set(false);
        Swal.fire({
          icon: 'success',
          title: 'Enlace reenviado',
          text: 'Se envió de nuevo el correo con el enlace de aprobación rápida.'
        });
      },
      error: (err) => {
        this.tokenResending.set(false);
        Swal.fire(
          'No se pudo reenviar',
          err.error?.error ?? err.error?.message ?? 'Intente nuevamente.',
          'error'
        );
      }
    });
  }
}
